import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHead from "@/components/PageHead";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { track } from "@/lib/analytics";
import { cloudStorage } from "@/lib/cloudStore";
import {
  Compass,

  FileText,
  Target,
  ArrowRight,
  Sparkles,
  MapPin,
  Palette,
  MessageCircle,
  CheckCircle2,
  Printer,
} from "lucide-react";

type Step = {
  num: number;
  title: string;
  time: string;
  what: string;
  why: string;
  clicks: string[];
  to: string;
  cta: string;
  ctaAria: string;
  icon: React.ComponentType<{ className?: string }>;
};

const STEPS: Step[] = [
  {
    num: 1,
    title: "Take the Career Clarity 360™ assessment",
    time: "~15 minutes",
    what: "The flagship adaptive assessment every student begins with — purpose, personality, skills, and interests.",
    why: "This is the single source of truth. The report, dashboard, and AI coach all personalise from these answers.",
    clicks: [
      "Click the big button below to open /assessment",
      "Pick any age band and swipe through the cards",
      "You can stop after ~3 minutes and still see a report",
    ],
    to: "/start",
    cta: "Start the assessment",
    ctaAria: "Start the Career Clarity 360 assessment — step 1 of the student tour",
    icon: Compass,
  },
  {
    num: 2,
    title: "Read the AI-generated student report",
    time: "~5 minutes to skim",
    what: "The personalised narrative, archetype radar, matched careers, colleges, scholarships, and a skill roadmap.",
    why: "This is what parents and students receive on results day. Show principals the depth of AI personalisation here.",
    clicks: [
      "Open /report to see the sample report",
      "Scroll through Archetype Radar → Careers → Colleges → Skill Roadmap",
      "Use the 'Share' button to see the public read-only view",
    ],
    to: "/report",
    cta: "Open the sample report",
    ctaAria: "Open the sample student report — step 2 of the student tour",
    icon: FileText,
  },
  {
    num: 3,
    title: "Explore the Student Dashboard",
    time: "~3 minutes",
    what: "The learner's daily home — saved careers, XP, streaks, shortcuts, and next-best actions.",
    why: "This is where students return every day. It's the retention layer that turns a one-off test into a habit.",
    clicks: [
      "Open /dashboard",
      "Check the XP panel and saved careers",
      "Use the shortcuts to jump into Journey, Vision Board, or Coach",
    ],
    to: "/dashboard",
    cta: "Open student dashboard",
    ctaAria: "Open the student dashboard — step 3 of the student tour",
    icon: Target,
  },
];

const EXTRAS = [
  { title: "Journey", desc: "Timeline of milestones and reflections.", to: "/journey", icon: MapPin },
  { title: "Vision Board", desc: "Aspirational board students build over time.", to: "/vision-board", icon: Palette },
  { title: "AI Coach", desc: "1:1 conversational coach grounded in the student's data.", to: "/coach", icon: MessageCircle },
];

const CHECKLIST: { id: string; label: string }[] = [
  { id: "assessment", label: "Completed at least 3 minutes of the /assessment" },
  { id: "report", label: "Skimmed the sample /report end-to-end" },
  { id: "dashboard", label: "Opened the student /dashboard and saw XP + saved careers" },
  { id: "journey", label: "Peeked at /journey to see the milestone timeline" },
  { id: "vision", label: "Opened /vision-board to see the aspirational canvas" },
  { id: "coach", label: "Sent one message to the AI /coach" },
];

const STORAGE_KEY = "wekit_student_quickstart_checklist";

function handleCta(step: "assessment" | "report" | "dashboard", position: string) {
  track("principal_demo_student_cta_click", {
    surface: "student_quickstart",
    props: { step, position },
  });
}

export default function StudentQuickstart() {
  const [checked, setChecked] = useState<Record<string, boolean>>({});

  useEffect(() => {
    try {
      const raw = cloudStorage.getItem(STORAGE_KEY);
      if (raw) setChecked(JSON.parse(raw));
    } catch {
      /* ignore */
    }
    track("student_quickstart_view", { surface: "student_quickstart" });
  }, []);

  const toggle = (id: string) => {
    setChecked((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try {
        cloudStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const doneCount = CHECKLIST.filter((c) => checked[c.id]).length;

  return (
    <>
      <PageHead
        title="Student Quickstart — Career Clarity 360™ | WeKIT™"
        description="A step-by-step quickstart for principals to experience Career Clarity 360™ exactly as a student would."
        path="/student-quickstart"
      />
      <main className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-6 py-16">
          {/* Hero */}
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs text-muted-foreground mb-4">
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> Student experience tour · 3 steps · ~20 min
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-3">
              Try Career Clarity 360™ as a student
            </h1>
            <p className="text-lg text-muted-foreground">
              This is your first check-in of a 12-month Career Growth Journey — the exact
              product every student in your school will use. Nothing hidden, nothing mocked.
              After this, learners return every 3 months to track how their strengths, confidence,
              and career readiness evolve.
            </p>
          </div>

          {/* Jump bar: assessment is the primary; report & dashboard are secondary follow-ups */}
          <section
            aria-labelledby="jump-bar-heading"
            className="rounded-2xl border-2 border-primary/40 bg-primary/5 p-6 mb-10 focus-within:ring-2 focus-within:ring-primary/40"
          >
            <h2
              id="jump-bar-heading"
              className="text-xs uppercase tracking-wide text-primary font-semibold mb-3"
            >
              Jump straight in
            </h2>

            {/* Primary action — assessment */}
            <Button
              asChild
              size="lg"
              className="w-full h-16 text-base focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <Link
                to="/start"
                aria-label="Primary action: start the student assessment at /assessment"
                onClick={() => handleCta("assessment", "jump_bar_primary")}
              >
                <Compass className="mr-2 h-5 w-5" aria-hidden="true" />
                <span className="flex flex-col items-start leading-tight">
                  <span className="font-semibold">Start here — Take the assessment</span>
                  <span className="text-xs opacity-90 font-normal">
                    /assessment · ~15 min · the required first step
                  </span>
                </span>
                <ArrowRight className="ml-auto h-5 w-5" aria-hidden="true" />
              </Link>
            </Button>

            {/* Secondary follow-ups */}
            <div className="grid gap-3 sm:grid-cols-2 mt-3">
              <Button
                asChild
                variant="secondary"
                className="h-auto py-3 justify-start focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <Link
                  to="/report"
                  aria-label="Then open the sample AI-generated student report at /report"
                  onClick={() => handleCta("report", "jump_bar_secondary")}
                >
                  <FileText className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="flex flex-col items-start leading-tight text-left">
                    <span className="font-semibold text-sm">Then: See the AI report</span>
                    <span className="text-xs opacity-80 font-normal">
                      /report · what parents receive
                    </span>
                  </span>
                </Link>
              </Button>
              <Button
                asChild
                variant="secondary"
                className="h-auto py-3 justify-start focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <Link
                  to="/dashboard"
                  aria-label="Finally open the student dashboard at /dashboard"
                  onClick={() => handleCta("dashboard", "jump_bar_secondary")}
                >
                  <Target className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="flex flex-col items-start leading-tight text-left">
                    <span className="font-semibold text-sm">Finally: Student dashboard</span>
                    <span className="text-xs opacity-80 font-normal">
                      /dashboard · daily retention home
                    </span>
                  </span>
                </Link>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Do them in order — the report and dashboard get much richer once you've completed the
              assessment.
            </p>
          </section>

          {/* Printable checklist */}
          <section
            aria-labelledby="checklist-heading"
            className="rounded-2xl border p-6 mb-10 print:border-0 print:p-0"
          >
            <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
              <div>
                <h2 id="checklist-heading" className="text-xl font-semibold">
                  Your review checklist
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Track what you've already explored. Progress is saved locally in your browser.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span
                  className="text-sm text-muted-foreground"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {doneCount} of {CHECKLIST.length} reviewed
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.print()}
                  aria-label="Print the student flow review checklist"
                  className="print:hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <Printer className="mr-2 h-4 w-4" aria-hidden="true" /> Print
                </Button>
              </div>
            </div>
            <ul className="space-y-2" aria-label="Student flow review checklist">
              {CHECKLIST.map((item) => {
                const inputId = `checklist-${item.id}`;
                return (
                  <li key={item.id} className="flex items-center gap-3">
                    <Checkbox
                      id={inputId}
                      checked={!!checked[item.id]}
                      onCheckedChange={() => toggle(item.id)}
                      aria-label={item.label}
                    />
                    <label
                      htmlFor={inputId}
                      className={`text-sm cursor-pointer select-none ${
                        checked[item.id] ? "line-through text-muted-foreground" : ""
                      }`}
                    >
                      {item.label}
                    </label>
                  </li>
                );
              })}
            </ul>
          </section>

          {/* Detailed step cards */}
          <h2 className="text-2xl font-semibold mb-4">Step-by-step walkthrough</h2>
          <ol className="space-y-5 mb-12 list-none p-0">
            {STEPS.map((s) => {
              const Icon = s.icon;
              const stepKey = s.to.replace("/", "") as "assessment" | "report" | "dashboard";
              return (
                <li key={s.num}>
                  <Card
                    role="article"
                    aria-labelledby={`step-${s.num}-title`}
                    className="overflow-hidden focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
                  >
                    <CardHeader className="flex flex-row items-start gap-4 space-y-0 bg-muted/30">
                      <div
                        className="h-12 w-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0 text-lg font-bold"
                        aria-hidden="true"
                      >
                        {s.num}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground mb-1">
                          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                          <span>
                            <span className="sr-only">Step {s.num} of 3 · </span>
                            {s.time}
                          </span>
                        </div>
                        <CardTitle id={`step-${s.num}-title`} className="text-xl">
                          {s.title}
                        </CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                            What it is
                          </div>
                          <p className="text-sm">{s.what}</p>
                        </div>
                        <div>
                          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                            Why it matters
                          </div>
                          <p className="text-sm">{s.why}</p>
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                          What to click
                        </div>
                        <ul className="space-y-1.5">
                          {s.clicks.map((c, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm">
                              <CheckCircle2
                                className="h-4 w-4 text-primary mt-0.5 shrink-0"
                                aria-hidden="true"
                              />
                              <span>{c}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <Button
                        asChild
                        size="lg"
                        className="w-full sm:w-auto focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        <Link
                          to={s.to}
                          aria-label={s.ctaAria}
                          onClick={() => handleCta(stepKey, "step_card")}
                        >
                          {s.cta} <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                </li>
              );
            })}
          </ol>

          {/* Extras */}
          <h2 className="text-xl font-semibold mb-4">Then explore the wider student world</h2>
          <ul className="grid gap-3 sm:grid-cols-3 mb-10 list-none p-0">
            {EXTRAS.map((s) => {
              const Icon = s.icon;
              return (
                <li key={s.to}>
                  <Link
                    to={s.to}
                    aria-label={`${s.title} — ${s.desc}`}
                    className="block rounded-lg border p-4 hover:border-primary/50 hover:bg-muted/40 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <Icon className="h-5 w-5 text-primary mb-2" aria-hidden="true" />
                    <div className="font-semibold text-sm">{s.title}</div>
                    <div className="text-xs text-muted-foreground mt-1">{s.desc}</div>
                  </Link>
                </li>
              );
            })}
          </ul>

          <div className="text-sm text-muted-foreground text-center border-t pt-6">
            Coming from the demo login? Return to{" "}
            <Link
              to="/principal-demo-access"
              className="underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              Principal Demo Access
            </Link>{" "}
            or open the{" "}
            <Link
              to="/institution"
              className="underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              Institution Dashboard
            </Link>
            .
          </div>
        </div>
      </main>
    </>
  );
}
